import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSiteSettings } from '../context/SiteSettingsContext';
import './WhatsAppButton.css';

/**
 * Floating WhatsApp contact pill — bottom-right of every page except
 * the checkout funnel and admin surfaces. In markets where WhatsApp is
 * the de-facto customer-support channel, a single tap to start a
 * thread carries strong conversion weight on a small-team store.
 *
 * Visibility rules:
 *  - Admin must enable it (`whatsAppEnabled` site setting). Default
 *    is OFF so a fresh install never accidentally exposes a phone.
 *  - Number resolves to `whatsAppPhone` first, then falls back to
 *    `storePhone` so a small team can manage one number; both come
 *    from admin → Settings.
 *  - Always normalised to digits-only for the wa.me URL.
 *  - Hidden on /checkout, /order-confirmation, /admin/*, /driver/*,
 *    and on print media (CSS).
 *
 * No pre-filled message — the conversation should start from the
 * customer's intent, not a templated greeting.
 */
const WhatsAppButton = () => {
  const { t } = useTranslation();
  const { whatsAppEnabled, whatsAppPhone, storePhone } = useSiteSettings();
  const { pathname } = useLocation();

  const isHiddenSurface =
    pathname.startsWith('/checkout') ||
    pathname.startsWith('/order-confirmation') ||
    pathname.startsWith('/admin') ||
    pathname.startsWith('/driver');

  if (isHiddenSurface) return null;
  if (!whatsAppEnabled) return null;

  // `whatsAppPhone` is the explicit override; `storePhone` is the
  // shared default for stores that use one number for everything.
  const raw = whatsAppPhone || storePhone || '';
  const phone = raw.replace(/[^\d]/g, '');
  if (!phone) return null;

  const href = `https://wa.me/${phone}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-button"
      aria-label={t('whatsapp.label', 'Chat with us on WhatsApp')}
    >
      {/* Brand SVG — `lucide-react` v1.x ships no WhatsApp glyph,
          so we inline the official mark scaled to match Lucide
          1.5px stroke conventions. Keep stroke="none" + fill so it
          reads as a brand pictogram, not a UI icon. */}
      <svg
        width="22"
        height="22"
        viewBox="0 0 32 32"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M16.003 3C9.376 3 4 8.371 4 14.99c0 2.36.683 4.564 1.864 6.434L4 29l7.776-1.812a12.02 12.02 0 0 0 4.227.768H16c6.626 0 12-5.371 12-11.99C28 8.371 22.629 3 16.003 3Zm0 21.96h-.004a9.96 9.96 0 0 1-3.804-.755l-.273-.108-4.61 1.073 1.097-4.488-.178-.288a9.94 9.94 0 0 1-1.524-5.42c0-5.499 4.477-9.974 9.997-9.974 2.668 0 5.176 1.039 7.064 2.926a9.91 9.91 0 0 1 2.926 7.06c0 5.5-4.477 9.974-9.991 9.974Zm5.477-7.473c-.302-.151-1.785-.881-2.062-.982-.276-.101-.477-.151-.679.151-.201.302-.778.982-.954 1.183-.176.201-.352.227-.654.075-.302-.151-1.275-.469-2.428-1.494-.897-.798-1.504-1.785-1.68-2.087-.176-.302-.019-.466.132-.617.135-.135.302-.352.453-.528.151-.176.201-.302.302-.503.101-.201.05-.378-.025-.528-.076-.151-.679-1.636-.93-2.241-.245-.589-.494-.509-.679-.519-.176-.008-.378-.01-.579-.01-.201 0-.528.075-.805.378-.276.302-1.057 1.033-1.057 2.519 0 1.486 1.082 2.923 1.233 3.124.151.201 2.13 3.252 5.16 4.561.721.311 1.284.497 1.723.636.724.23 1.382.198 1.903.12.581-.087 1.785-.729 2.038-1.434.252-.705.252-1.31.176-1.434-.075-.124-.276-.198-.578-.349Z" />
      </svg>
      <span className="whatsapp-button__label">{t('whatsapp.cta', 'WhatsApp')}</span>
    </a>
  );
};

export default WhatsAppButton;
